/**
 * useGiftEligibility — can this address receive a gifted pass?
 *
 * The answer is SERVER state, so it lives in react-query, keyed by the address —
 * never mirrored into component state. Callers pass the COMMITTED address (set
 * on blur), not the live input, so typing does not fire a request per keystroke.
 *
 * Advisory only. The purchase re-runs the same rules before any charge, so the
 * three-way result is deliberately lenient about not knowing:
 *   - `eligible === false`  the server said no → block Pay, show the vague line
 *   - `eligible === true`   the server said yes
 *   - `eligible === null`   no answer (guest, offline, failed) → do NOT block;
 *                           "no answer" is not a refusal, and the server is the gate
 *
 * @module services/internalApi/subscription/hooks/useGiftEligibility
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { passService } from '../api/passService';

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useGiftEligibility(options: {
  clubId: number | null | undefined;
  /** The committed (blurred) address; empty/invalid disables the query. */
  email: string;
  /** Only an authenticated buyer may ask — the route is not a guest route. */
  enabled: boolean;
}) {
  const email = options.email.trim().toLowerCase();
  const canAsk = options.enabled && !!options.clubId && EMAIL_SHAPE.test(email);

  const query = useQuery({
    queryKey: canAsk
      ? queryKeys.subscriptions.giftEligibility(options.clubId as number, email)
      : queryKeys.disabled.giftEligibility(),
    queryFn: async () => {
      const result = await passService.checkGiftEligibility({ email, clubId: options.clubId as number });
      if (!result.success) throw new Error(result.message || 'Eligibility check failed');
      return result.payload;
    },
    enabled: canAsk,
    retry: false,
    ...cachePresets.frequent,
  });

  return {
    eligible: canAsk && query.data ? query.data.eligible : null,
    isChecking: canAsk && query.isLoading,
  };
}
